import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { iamApi } from '@/api/iam.api';
import { getNonAxiosErrorMessage } from '@/api/request';
import { useAuthStore } from '@/store/auth.store';
import { accountProfileToUserInfo } from '@/lib/mapIamProfile';

const schema = z.object({
  username: z.string().min(1, '主账号名称不能为空'),
  password: z.string().min(1, '密码不能为空'),
});

type FormData = z.infer<typeof schema>;

// TODO(auth): OIDC 已接管平台登录。迁移稳定后删除此旧登录页及其账号密码登录逻辑。
export default function AccountLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    setError('');
    setLoading(true);

    try {
      const tokenData = await iamApi.getAccountToken({
        user_type: 'account',
        username: data.username,
        password: data.password,
      });
      setToken(tokenData.access_token, tokenData.expires_sec);
      const profile = await iamApi.getAccountProfile(tokenData.access_token);
      setUser(accountProfileToUserInfo(profile, data.username));
      const redirectTo =
        (location.state as { from?: string } | null)?.from ||
        new URLSearchParams(window.location.search).get('redirect') ||
        '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const errorMessage = getNonAxiosErrorMessage(err, '登录失败，请重试');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-white">Agent 平台</span>
        </div>

        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            欢迎使用
            <br />
            Agent 开发平台
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            轻松管理和部署您的 AI Agent，实现智能化工作流程
          </p>
        </div>

        <div className="text-white/40 text-sm">© 2024 Agent Platform. All rights reserved.</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">主账号登录</h2>
            <p className="text-muted-foreground mt-2">使用主账号信息登录 Agent 开发平台</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="account-username" className="text-gray-700">
                主账号名称
              </Label>
              <Input
                id="account-username"
                type="text"
                placeholder="请输入主账号名称"
                {...register('username')}
                disabled={loading}
                className="h-12"
              />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-password" className="text-gray-700">
                密码
              </Label>
              <Input
                id="account-password"
                type="password"
                placeholder="请输入密码"
                {...register('password')}
                disabled={loading}
                className="h-12"
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full h-12 text-base" disabled={loading}>
              {loading ? '登录中...' : '登录'}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base"
              disabled={loading}
              onClick={() => navigate('/login/sso')}
            >
              SSO 登录
            </Button>
          </form>

          {/* <div className="mt-6 text-center">
            <span className="text-muted-foreground">没有账号？</span>
            <button
              type="button"
              onClick={() => navigate('/login/user')}
              className="ml-1 text-primary hover:underline font-medium"
            >
              IAM 用户登录
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
}
